import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BASE_URL } from "../../helpers/api";
import "./MockGateway.css";

const BANKS = [
  "State Bank of India", "HDFC Bank", "ICICI Bank", "Axis Bank",
  "Kotak Mahindra Bank", "Punjab National Bank", "Bank of Baroda", "Yes Bank",
];

const WALLETS = ["PhonePe", "Paytm", "Amazon Pay", "Mobikwik", "Freecharge"];

const METHODS = [
  { key: "card", icon: "\u{1F4B3}", title: "Card", sub: "Visa, Mastercard, RuPay & more" },
  { key: "upi", icon: "\u{1F4F1}", title: "UPI / QR", sub: "GPay, PhonePe, Paytm & more" },
  { key: "netbanking", icon: "\u{1F3E6}", title: "Netbanking", sub: "All Indian banks" },
  { key: "wallet", icon: "\u{1F45B}", title: "Wallet", sub: "PhonePe, Paytm, Amazon Pay" },
];

function formatCountdown(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Stands in for a real gateway's hosted checkout page (Stripe Checkout /
// Razorpay's popup / etc). Visually it mimics the payment-method selection
// screen a real gateway shows, so the flow is demoable before real keys
// exist — but nothing entered here (card/UPI/bank details) is sent or
// processed anywhere, and the QR code is a static placeholder, not a real
// scannable one. Once a real gateway is wired in, this page and the redirect
// that points to it are the only pieces that get swapped out; the
// create-order/verify/webhook contract on the backend stays the same.
export default function MockPaymentGateway() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const reference = searchParams.get("reference");
  const amount = Number(searchParams.get("amount") || 0);

  const [expanded, setExpanded] = useState("upi");
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [stage, setStage] = useState("select"); // select | pin | processing | success | failed
  const [countdown, setCountdown] = useState(8 * 60);
  const [pin, setPin] = useState("");

  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [cardName, setCardName] = useState("");
  const [upiId, setUpiId] = useState("");

  // Groups digits into "1234 5678 9012 3456", capped at 16 digits.
  const handleCardNumberChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 16);
    setCardNumber(digits.replace(/(.{4})/g, "$1 ").trim());
  };

  // Formats as MM/YY while typing, digits only.
  const handleExpiryChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
    setCardExpiry(digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits);
  };

  const isCardValid =
    cardNumber.replace(/\s/g, "").length === 16 &&
    /^\d{2}\/\d{2}$/.test(cardExpiry) &&
    cardCvv.length === 3 &&
    cardName.trim().length > 0;

  const isUpiValid = /^[\w.-]{2,256}@[a-zA-Z]{2,64}$/.test(upiId);

  useEffect(() => {
    if (stage !== "select" || countdown <= 0) return;
    const id = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [stage, countdown]);

  const complete = async (result) => {
    setStage("processing");
    try {
      await fetch(`${BASE_URL}/payments/mock-gateway/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference, result }),
      });
    } catch (err) {
      console.error("Error completing mock payment:", err);
    }

    setTimeout(() => {
      setStage(result === "success" ? "success" : "failed");
      setTimeout(() => {
        navigate(`/payment?reference=${encodeURIComponent(reference)}`);
      }, 1200);
    }, 1200);
  };

  if (!reference) {
    return <p style={{ textAlign: "center", marginTop: 60 }}>Missing payment reference.</p>;
  }

  const canPay =
    (expanded === "upi" && isUpiValid) ||
    (expanded === "card" && isCardValid) ||
    (expanded === "netbanking" && selectedBank) ||
    (expanded === "wallet" && selectedWallet);

  const pinLabel =
    expanded === "netbanking" ? `Enter your ${selectedBank} net banking PIN`
    : expanded === "wallet" ? `Enter your ${selectedWallet} PIN`
    : expanded === "card" ? "Enter the OTP sent to your registered mobile"
    : "Enter your UPI PIN";

  return (
    <div className="mgw-overlay">
      <div className="mgw-modal">
        <div className="mgw-header">
          <div className="mgw-header-logo">M</div>
          <div>
            <div className="mgw-header-name">Medayus</div>
            <div className="mgw-header-sub">Local Test Gateway (no real payment)</div>
          </div>
        </div>

        {stage === "select" && (
          <>
            <div className="mgw-body">
              <div className="mgw-section-title">Pay With UPI QR</div>
              <div className="mgw-qr" />
              <div className="mgw-qr-caption">Scan with any UPI app</div>
              {countdown > 0 ? (
                <div className="mgw-qr-timer">QR code is valid for {formatCountdown(countdown)} minutes</div>
              ) : (
                <div className="mgw-qr-timer">QR code expired — pick another method below</div>
              )}

              <div className="mgw-divider" />
              <div className="mgw-section-title">Cards, UPI &amp; More</div>

              {METHODS.map((m) => (
                <div className="mgw-method" key={m.key}>
                  <div
                    className={`mgw-method-head ${expanded === m.key ? "active" : ""}`}
                    onClick={() => setExpanded(expanded === m.key ? null : m.key)}
                  >
                    <div className="mgw-method-icon">{m.icon}</div>
                    <div className="mgw-method-text">
                      <div className="mgw-method-title">{m.title}</div>
                      <div className="mgw-method-sub">{m.sub}</div>
                    </div>
                    <div className={`mgw-chevron ${expanded === m.key ? "open" : ""}`}>&#9654;</div>
                  </div>

                  {expanded === m.key && (
                    <div className="mgw-method-panel">
                      {m.key === "card" && (
                        <>
                          <input
                            className="mgw-field"
                            placeholder="Card Number"
                            inputMode="numeric"
                            value={cardNumber}
                            onChange={handleCardNumberChange}
                            maxLength={19}
                          />
                          <div className="mgw-row">
                            <input
                              className="mgw-field"
                              placeholder="MM / YY"
                              inputMode="numeric"
                              value={cardExpiry}
                              onChange={handleExpiryChange}
                              maxLength={5}
                            />
                            <input
                              className="mgw-field"
                              placeholder="CVV"
                              inputMode="numeric"
                              type="password"
                              value={cardCvv}
                              onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, "").slice(0, 3))}
                              maxLength={3}
                            />
                          </div>
                          <input
                            className="mgw-field"
                            placeholder="Name on Card"
                            value={cardName}
                            onChange={(e) => setCardName(e.target.value.replace(/[^a-zA-Z .]/g, "").slice(0, 40))}
                            maxLength={40}
                          />
                        </>
                      )}

                      {m.key === "upi" && (
                        <input
                          className="mgw-field"
                          placeholder="Enter UPI ID (e.g. name@okhdfcbank)"
                          value={upiId}
                          onChange={(e) => setUpiId(e.target.value.replace(/\s/g, "").slice(0, 60))}
                          maxLength={60}
                        />
                      )}

                      {m.key === "netbanking" && (
                        <div className="mgw-option-list">
                          {BANKS.map((bank) => (
                            <label key={bank} className={`mgw-option ${selectedBank === bank ? "selected" : ""}`}>
                              <input
                                type="radio"
                                name="bank"
                                checked={selectedBank === bank}
                                onChange={() => setSelectedBank(bank)}
                              />
                              {bank}
                            </label>
                          ))}
                        </div>
                      )}

                      {m.key === "wallet" && (
                        <div className="mgw-option-list">
                          {WALLETS.map((wallet) => (
                            <label key={wallet} className={`mgw-option ${selectedWallet === wallet ? "selected" : ""}`}>
                              <input
                                type="radio"
                                name="wallet"
                                checked={selectedWallet === wallet}
                                onChange={() => setSelectedWallet(wallet)}
                              />
                              {wallet}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mgw-footer">
              <div>
                <div className="mgw-footer-amount">₹{amount}</div>
                <div className="mgw-footer-ref">Ref: {reference}</div>
              </div>
              <button className="mgw-pay-btn" disabled={!canPay} onClick={() => setStage("pin")}>
                Pay Now
              </button>
            </div>
            <div className="mgw-disclaimer">
              No real gateway is connected. Nothing entered above is sent anywhere —
              this screen only demonstrates the payment flow end to end.
            </div>
          </>
        )}

        {stage === "pin" && (
          <div className="mgw-status">
            <div className="mgw-footer-amount" style={{ marginBottom: 4 }}>₹{amount}</div>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 18 }}>{pinLabel} to authorize this payment</p>
            <input
              className="mgw-field"
              type="password"
              inputMode="numeric"
              maxLength={6}
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              style={{ maxWidth: 160, textAlign: "center", letterSpacing: 6, fontSize: 18 }}
              autoFocus
            />
            <button
              className="mgw-pay-btn"
              disabled={pin.length < 4}
              onClick={() => complete("success")}
              style={{ marginTop: 10, width: "100%", maxWidth: 200 }}
            >
              Confirm Payment
            </button>
            <span className="mgw-fail-link" onClick={() => complete("failure")}>
              (testing) simulate wrong PIN / failure instead
            </span>
          </div>
        )}

        {stage === "processing" && (
          <div className="mgw-status">
            <div className="mgw-spinner" />
            <p>Processing payment…</p>
          </div>
        )}

        {stage === "success" && (
          <div className="mgw-status">
            <div className="mgw-check">&#10003;</div>
            <p><strong>Payment Successful</strong></p>
            <p style={{ fontSize: 13, color: "#888" }}>Redirecting…</p>
          </div>
        )}

        {stage === "failed" && (
          <div className="mgw-status">
            <div className="mgw-cross">&#10007;</div>
            <p><strong>Payment Failed</strong></p>
            <p style={{ fontSize: 13, color: "#888" }}>Redirecting…</p>
          </div>
        )}
      </div>
    </div>
  );
}
